import type { TranscriptionJob, TranslationJob } from '../../types/videos';
import { TranscriptionJobBadge } from './TranscriptionJobBadge';
import { TranscriptLanguageSwitch } from './TranscriptLanguageSwitch';

interface TranscriptPanelProps {
  transcript: string | null;
  job: TranscriptionJob | null;
  onTranscribe: () => void;
  languages?: string[];
  activeLang?: string | null;
  originalLang?: string | null;
  onLanguageChange?: (lang: string | null) => void;
  translationJob?: TranslationJob | null;
  onTranslate?: () => void;
}

export function TranscriptPanel({
  transcript,
  job,
  onTranscribe,
  languages = [],
  activeLang = null,
  originalLang = null,
  onLanguageChange,
  translationJob,
  onTranslate,
}: TranscriptPanelProps) {
  const isTranscribeBusy = job?.status === 'queued' || job?.status === 'running';
  const isTranslateBusy = translationJob?.status === 'queued' || translationJob?.status === 'running';

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-gray-900">Transcript</h2>
        <div className="flex items-center gap-2">
          {onLanguageChange && (
            <TranscriptLanguageSwitch
              languages={languages}
              active={activeLang}
              original={originalLang}
              onChange={onLanguageChange}
              disabled={isTranslateBusy}
            />
          )}
          <TranscriptionJobBadge status={job?.status ?? null} error={job?.error} />
          <TranscriptionJobBadge status={translationJob?.status ?? null} error={translationJob?.error} />
          {onTranslate && originalLang && originalLang !== 'en' && (
            <button
              onClick={onTranslate}
              disabled={isTranslateBusy}
              className="text-xs px-3 py-1.5 rounded border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isTranslateBusy ? 'Translating…' : 'Translate to English'}
            </button>
          )}
          <button
            onClick={onTranscribe}
            disabled={isTranscribeBusy}
            className="text-xs px-3 py-1.5 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {transcript ? 'Re-transcribe' : 'Transcribe'}
          </button>
        </div>
      </div>

      {transcript ? (
        <div className="max-h-96 overflow-y-auto">
          <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans leading-relaxed">{transcript}</pre>
        </div>
      ) : (
        <div className="text-center py-12 border border-dashed border-gray-200 rounded-lg">
          <p className="text-sm text-gray-500 mb-3">No transcript yet.</p>
          <button
            onClick={onTranscribe}
            disabled={isTranscribeBusy}
            className="text-sm px-4 py-2 rounded bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isTranscribeBusy ? 'Transcribing…' : 'Generate transcript'}
          </button>
        </div>
      )}
    </div>
  );
}
